import { Collapse } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { SvgIconTypeMap } from '@mui/material/SvgIcon';
import React from 'react';

import useLeftNavContext from './left-nav-context';
import { LeftNavFlyoutMenu } from './left-nav-flyout-menu';
import { LeftNavMenuItem } from './left-nav-menu-item';
import { LeftNavSubmenuProvider } from './left-nav-submenu-context';

interface Props {
  /**
   * Icon for the parent menu item
   */
  icon: OverridableComponent<SvgIconTypeMap>;
  /**
   * Title for the parent menu item
   */
  title: React.ReactNode;
  /**
   * Optional title for the flyout menu (when minimized)
   * If not provided, will use the title prop
   */
  flyoutTitle?: React.ReactNode;
  /**
   * Icon to show when expanded
   */
  expandedIcon: OverridableComponent<SvgIconTypeMap>;
  /**
   * Icon to show when collapsed
   */
  collapsedIcon: OverridableComponent<SvgIconTypeMap>;
  /**
   * Whether this menu group is expanded
   */
  expanded: boolean;
  /**
   * Callback when the expansion state should change
   */
  onToggle: () => void;
  /**
   * Menu items to show in the submenu
   */
  children: React.ReactNode;
}

/**
 * Menu group that handles both expanded/collapsed state in full sidebar
 * and flyout menu in minimized sidebar
 */
export const LeftNavMenuGroup: React.FC<Props> = ({
  icon,
  title,
  flyoutTitle,
  expandedIcon,
  collapsedIcon,
  expanded,
  onToggle,
  children,
}) => {
  const { state } = useLeftNavContext();
  const { navColumnOpen } = state;

  const [flyoutState, setFlyoutState] = React.useState<{
    anchorEl: HTMLElement | null;
    open: boolean;
  }>({ anchorEl: null, open: false });

  // Reset flyout state when sidebar expand/collapse state changes
  // to prevent stale anchorEl references causing mispositioned flyouts
  React.useEffect(() => {
    setFlyoutState({ anchorEl: null, open: false });
  }, [navColumnOpen]);

  const handleGroupToggleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!navColumnOpen) {
      // Toggle flyout on click when minimized
      setFlyoutState((prev) => ({
        anchorEl: prev.open ? null : event.currentTarget,
        open: !prev.open,
      }));
    } else {
      // Normal toggle behavior when expanded
      onToggle();
    }
  };

  const handleFlyoutClose = () => {
    setFlyoutState({ anchorEl: null, open: false });
  };

  return (
    <>
      <LeftNavMenuItem
        icon={icon}
        onClick={handleGroupToggleClick}
        rightIcon={
          navColumnOpen ? (expanded ? expandedIcon : collapsedIcon) : undefined
        }
        aria-expanded={navColumnOpen ? expanded : flyoutState.open}
        aria-haspopup={!navColumnOpen ? 'menu' : undefined}
        aria-controls={
          !navColumnOpen && flyoutState.open ? 'flyout-menu' : undefined
        }
      >
        {title}
      </LeftNavMenuItem>

      {/* Flyout menu for minimized sidebar */}
      {!navColumnOpen && (
        <LeftNavFlyoutMenu
          anchorEl={flyoutState.anchorEl}
          open={flyoutState.open}
          onClose={handleFlyoutClose}
          title={flyoutTitle ?? title}
        >
          <LeftNavSubmenuProvider isInline={false}>
            {children}
          </LeftNavSubmenuProvider>
        </LeftNavFlyoutMenu>
      )}

      {/* Collapse menu for expanded sidebar */}
      {navColumnOpen && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <LeftNavSubmenuProvider isInline>
            <div className="submenu-container">{children}</div>
          </LeftNavSubmenuProvider>
        </Collapse>
      )}
    </>
  );
};
